import { MantineGradient } from "@mantine/core";

export type ThemeTokens = {
  colors: {
    primary: string;
    primary1: string;
    primary6: string;
    primary7: string;
    primary8: string;
    secondary: string;
    secondary4: string;
    success: string;
    warning: string;
    danger: string;
    accent: string;
    neutral: string;
    background: string;
    surface: string;
    surfaceMuted: string;
    surfaceSoft: string;
    textSecondary: string;
    textMuted: string;
    borderMuted: string;
    primaryAlt: string;
    textStrong: string;
    black: string;
    white: string;
    premiumAqua: string;
    premiumPink: string;
    premiumOrange: string;
    successAlt: string;
    tooltip: string;
  };
  palette: {
    primary: Tuple10;
    primaryAlt: Tuple10;
    secondary: Tuple10;
    success: Tuple10;
    warning: Tuple10;
    danger: Tuple10;
    accent: Tuple10;
    neutral: Tuple10;
    surface: Tuple10;
    surfaceMuted: Tuple10;
    surfaceSoft: Tuple10;
    textSecondary: Tuple10;
    textMuted: Tuple10;
    borderMuted: Tuple10;
    textStrong: Tuple10;
    black: Tuple10;
    white: Tuple10;
    successAlt: Tuple10;
    premiumAqua: Tuple10;
    premiumPink: Tuple10;
    premiumOrange: Tuple10;
    tooltip: Tuple10;
  };
  gradients: {
    primary: MantineGradient;
    premium: MantineGradient;
    secondary: MantineGradient;
  };
  typography: {
    fontFamily: string;
    fontFamilyHeadings: string;
  };
};

/**
 * Fonte única de cores e gradientes do tema.
 */
const PRIMARY_HEX = "#1F6FEB";
const SECONDARY_HEX = "#14B8A6";
const SUCCESS_HEX = "#2F9E44";
const WARNING_HEX = "#F08C00";
const DANGER_HEX = "#E03131";
const ACCENT_HEX = "#E9457A";
const NEUTRAL_HEX = "#343A40";
const BACKGROUND_HEX = "#F8FAFC";
const SURFACE_HEX = "#F8F9FA";
const SURFACE_MUTED_HEX = "#E9ECEF";
const SURFACE_SOFT_HEX = "#F9F9F9";
const TEXT_SECONDARY_HEX = "#495057";
const TEXT_MUTED_HEX = "#6C757D";
const BORDER_MUTED_HEX = "#CDD1DC";
const PRIMARY_ALT_HEX = "#2056F4";
const TEXT_STRONG_HEX = "#151619";
const BLACK_HEX = "#000000";
const SUCCESS_ALT_HEX = "#00FF73";
const TOOLTIP_HEX = "#1D1F24";
const PRIMARY1_HEX = "#1F6FEB";
const PRIMARY6_HEX = "#1F6FEB";
const PRIMARY7_HEX = "#1F6FEB";
const PRIMARY8_HEX = "#1F6FEB";
const SECONDARY4_HEX = "#14B8A6";

const PREMIUM_AQUA_HEX = "#68C2C1";
const PREMIUM_PINK_HEX = "#E9457A";
const PREMIUM_ORANGE_HEX = "#EF7A5C";

type Tuple10 = [string, string, string, string, string, string, string, string, string, string];
const fillPalette = (color: string): Tuple10 => [color, color, color, color, color, color, color, color, color, color];

const primaryPalette = fillPalette(PRIMARY_HEX);
primaryPalette[1] = PRIMARY1_HEX;
primaryPalette[6] = PRIMARY6_HEX;
primaryPalette[7] = PRIMARY7_HEX;
primaryPalette[8] = PRIMARY8_HEX;
const primaryAltPalette = fillPalette(PRIMARY_ALT_HEX);
const secondaryPalette = fillPalette(SECONDARY_HEX);
secondaryPalette[4] = SECONDARY4_HEX;
const successPalette = fillPalette(SUCCESS_HEX);
const warningPalette = fillPalette(WARNING_HEX);
const dangerPalette = fillPalette(DANGER_HEX);
const accentPalette = fillPalette(ACCENT_HEX);
const neutralPalette = fillPalette(NEUTRAL_HEX);
const whitePalette = fillPalette("#FFFFFF");
const surfacePalette = fillPalette(SURFACE_HEX);
const surfaceMutedPalette = fillPalette(SURFACE_MUTED_HEX);
const surfaceSoftPalette = fillPalette(SURFACE_SOFT_HEX);
const textSecondaryPalette = fillPalette(TEXT_SECONDARY_HEX);
const textMutedPalette = fillPalette(TEXT_MUTED_HEX);
const borderMutedPalette = fillPalette(BORDER_MUTED_HEX);
const textStrongPalette = fillPalette(TEXT_STRONG_HEX);
const blackPalette = fillPalette(BLACK_HEX);
const successAltPalette = fillPalette(SUCCESS_ALT_HEX);
const tooltipPalette = fillPalette(TOOLTIP_HEX);

const premiumAquaPalette = fillPalette(PREMIUM_AQUA_HEX);
const premiumPinkPalette = fillPalette(PREMIUM_PINK_HEX);
const premiumOrangePalette = fillPalette(PREMIUM_ORANGE_HEX);

export const themeTokens: ThemeTokens = {
  colors: {
    primary: PRIMARY_HEX,
    primary1: PRIMARY1_HEX,
    primary6: PRIMARY6_HEX,
    primary7: PRIMARY7_HEX,
    primary8: PRIMARY8_HEX,
    secondary: SECONDARY_HEX,
    secondary4: SECONDARY4_HEX,
    success: SUCCESS_HEX,
    warning: WARNING_HEX,
    danger: DANGER_HEX,
    accent: ACCENT_HEX,
    neutral: NEUTRAL_HEX,
    background: BACKGROUND_HEX,
    surface: SURFACE_HEX,
    surfaceMuted: SURFACE_MUTED_HEX,
    surfaceSoft: SURFACE_SOFT_HEX,
    textSecondary: TEXT_SECONDARY_HEX,
    textMuted: TEXT_MUTED_HEX,
    borderMuted: BORDER_MUTED_HEX,
    primaryAlt: PRIMARY_ALT_HEX,
    textStrong: TEXT_STRONG_HEX,
    black: BLACK_HEX,
    white: "#FFFFFF",
    successAlt: SUCCESS_ALT_HEX,
    premiumAqua: PREMIUM_AQUA_HEX,
    premiumPink: PREMIUM_PINK_HEX,
    premiumOrange: PREMIUM_ORANGE_HEX,
    tooltip: TOOLTIP_HEX
  },
  palette: {
    primary: primaryPalette,
    primaryAlt: primaryAltPalette,
    secondary: secondaryPalette,
    success: successPalette,
    warning: warningPalette,
    danger: dangerPalette,
    accent: accentPalette,
    neutral: neutralPalette,
    surface: surfacePalette,
    surfaceMuted: surfaceMutedPalette,
    surfaceSoft: surfaceSoftPalette,
    textSecondary: textSecondaryPalette,
    textMuted: textMutedPalette,
    borderMuted: borderMutedPalette,
    textStrong: textStrongPalette,
    black: blackPalette,
    white: whitePalette,
    successAlt: successAltPalette,
    premiumAqua: premiumAquaPalette,
    premiumPink: premiumPinkPalette,
    premiumOrange: premiumOrangePalette,
    tooltip: tooltipPalette
  },
  gradients: {
    primary: {
      from: primaryPalette[5],
      to: primaryPalette[7],
      deg: 135
    },
    premium: {
      from: PREMIUM_AQUA_HEX,
      to: PREMIUM_PINK_HEX,
      deg: 135
    },
    secondary: {
      from: secondaryPalette[4],
      to: primaryPalette[6],
      deg: 135
    }
  },
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    fontFamilyHeadings: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
  }
};
